<script setup lang="ts">
import { computed, useSlots } from 'vue';
import Button from 'primevue/button';

import type { UiButtonSeverity, UiButtonSize, UiButtonVariant } from './ui-button.types';

defineOptions({ name: 'UiButton', inheritAttrs: false });

interface Props {
  /**
   * Button text. Omitting it makes the button icon-only, which Figma renders as
   * a circle. There is deliberately no default slot: PrimeVue's replaces the
   * whole inner render, which silently drops both the icon and the loading
   * spinner.
   */
  label?: string;
  /** Colour ramp. See `ui-button.types` for why fill is a separate axis. */
  severity?: UiButtonSeverity;
  /** Fill style. `ghost` is PrimeVue's `text`. */
  variant?: UiButtonVariant;
  /**
   * `m` (38px) and `s` (32px) take labels; `xs` (24px) is icon-only in Figma.
   */
  size?: UiButtonSize;
}

const { label, severity = 'primary', variant = 'solid', size = 'm' } = defineProps<Props>();

const slots = useSlots();

const PRIME_VARIANT = {
  outlined: 'outlined',
  ghost: 'text',
  link: 'link',
} as const;

const isIconOnly = computed(() => label === undefined);

// PrimeVue treats the primary ramp as the absence of a severity.
const primeSeverity = computed(() => (severity === 'primary' ? undefined : severity));

const primeVariant = computed(() => (variant === 'solid' ? undefined : PRIME_VARIANT[variant]));

const PRIME_SIZE = { s: 'small', l: 'large' } as const;

// PrimeVue only emits a class for `small`/`large`, so `xs` is carried locally.
const primeSize = computed(() =>
  size in PRIME_SIZE ? PRIME_SIZE[size as keyof typeof PRIME_SIZE] : undefined,
);
</script>

<template>
  <Button
    v-bind="{ ...$attrs, class: undefined }"
    :label="label"
    :severity="primeSeverity"
    :variant="primeVariant"
    :size="primeSize"
    rounded
    :class="[
      'ui-button',
      `ui-button--${size}`,
      { 'ui-button--icon-only': isIconOnly },
      $attrs.class,
    ]"
  >
    <template v-if="slots.icon" #icon="slotProps">
      <span :class="[slotProps.class, 'ui-button__icon']">
        <slot name="icon" v-bind="slotProps" />
      </span>
    </template>
  </Button>
</template>

<style scoped lang="scss">
.ui-button {
  white-space: nowrap;
}

// Icons reach the button through a slot, so PrimeVue's `.p-button-icon` class
// is only offered as a slot prop and never lands on the SVG itself. Binding it
// here is what lets icon size follow `size` instead of each call site.
.ui-button__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: $icon-size-sm;
  height: $icon-size-sm;

  :deep(svg) {
    width: 100%;
    height: 100%;
  }

  // An SVG here is clipped to the box above, but a font icon draws to its own
  // em box and so reads a size larger beside one. Stepping it down lines the
  // two up wherever they sit together.
  :deep(.pi) {
    font-size: $icon-size-xs;
  }
}

// Figma pairs the 32px text button with a 14px icon...
.ui-button--s .ui-button__icon {
  width: $icon-size-xs;
  height: $icon-size-xs;
}

// ...but every icon-only button keeps a 16px glyph; only the circle shrinks.
.ui-button--icon-only .ui-button__icon {
  width: $icon-size-sm;
  height: $icon-size-sm;
}

// Font icons arrive via the `icon` prop, so they land on `.p-button-icon`
// directly and miss the slot wrapper above.
.ui-button--icon-only :deep(.p-button-icon) {
  font-size: $icon-size-sm;
}

// PrimeVue has no `xs`, so Figma's 24px circle is defined here. The number has
// no counterpart in the spacing scale.
.ui-button--xs.ui-button--icon-only {
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
}
</style>
